import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FieldErrors } from 'react-hook-form';
import * as yup from 'yup';
import {
  ControlledDateInput,
  ControlledInput,
  getError as getFormError,
} from '../../../../components/form7';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import { currentYearPeriod, toMonthRange } from '../../../../utils/date';
import {
  useEmployeeAbsencesRefetchOption,
  useMyAbsencesRefetchOption,
} from '../../pages/Absences/useMyAbsencesRefetchOption';
import {
  AbsenceType,
  AbsenceOverlappingType,
  FieldType,
  FileInput,
  UpsertAbsenceInput,
  useUpsertAbsenceMutation,
  useValidateRequestAbsenceMutation,
} from '../../__generated__/graphql';
import { ModalSidebarFormContent7 } from '../ModalSidebar/ModalSidebarFormContent7';
import { AbsenceFormProps } from './AbsenceFormProps';
import FormBody from './FormBody';
import FormButtons from './FormButtons';
import OverlappingAbsences from './OverlappingAbsences';
import RequestingAmountResult from './RequestingAmountResult';
import SpecialRulesCard from './SpecialRulesCard';
import {
  getOverlappingMessage,
  isDocumentUploadVisible,
  calculateDaysTimes,
} from './helper';
import { AbsenceFileField } from './AbsenceFileField';
import { Maybe } from '../../../../utils/maybe';
import { RecordedLoomVideo } from '../LoomVideo';
import { useUserInfo } from '../../context/UserInfo';
import { useEmployeeIdContext } from '../../context/EmployeeIdContext';
import { collectFormDataExtraFields } from '../ExtraFields/logic';
import {
  AbsenceFormContext,
  useAbsenceFieldsForm,
} from './useAbsenceFieldsForm';
import {
  ExtraFieldContextParam,
  extraFieldsContextParams,
  ExtraFieldsSupportedObject,
} from '../ExtraFields';
import { ExtraFieldsArea7 } from '../ExtraFields/ExtraFieldsArea/ExtraFieldsArea7';
import { ExtraFieldsFormId } from '../ExtraFields/types';
import { DayTime } from './types';
import { AbsenceDefaultValues } from '../EditAbsenceSidebar/types';
import { mapToExtraFieldInput } from '../ExtraFields/extraFieldInternalValue';

export type FormData = {
  startDate: Date;
  endDate: Date;
  note?: string;
  loomVideo?: Maybe<RecordedLoomVideo>;
  [key: string]: any; // For dynamic X_startTime and X_endTime fields
};

const absenceFormContext: AbsenceFormContext = {
  requireNote: false,
};

const getDefaultValues = (defaultValues?: AbsenceDefaultValues) => {
  if (!defaultValues) {
    return {};
  }

  const base: Record<string, any> = {
    startDate: defaultValues.startDate
      ? new Date(defaultValues.startDate)
      : undefined,
    endDate: defaultValues.endDate
      ? new Date(defaultValues.endDate)
      : undefined,
    note: defaultValues.note,
  };

  if (defaultValues.daysTimes && defaultValues.daysTimes.length > 0) {
    for (const dt of defaultValues.daysTimes) {
      const dateKey = dt.currentDate;
      base[`${dateKey}_startTime`] = dt.startTime;
      base[`${dateKey}_endTime`] = dt.endTime;
    }
  }

  return base;
};

const mapFormToInput = (
  formData: FormData & {
    attachedFile?: FileInput | null;
    daysTimes?: DayTime[];
  } & Pick<
      AbsenceFormProps,
      | 'categoryId'
      | 'policyId'
      | 'absenceId'
      | 'employeeId'
      | 'documentCategoryId'
    >,
): UpsertAbsenceInput => {
  const {
    categoryId,
    policyId,
    absenceId,
    startDate,
    endDate,
    note,
    attachedFile,
    loomVideo,
    daysTimes = [],
    employeeId,
    documentCategoryId,
  } = formData;

  const startTime = daysTimes[0]?.startTime;
  const endTime = daysTimes[daysTimes.length - 1]?.endTime;

  return {
    id: absenceId ?? null,
    employeeId: employeeId ?? null,
    flair__Employee_Absence_Category__c: categoryId,
    flair__Employee_Absence_Request_Policy__c: policyId,
    flair__Start_Date__c: moment(startDate).format('YYYY-MM-DD'),
    flair__Start_Time__c: startTime,
    flair__End_Date__c: moment(endDate).format('YYYY-MM-DD'),
    flair__End_Time__c: endTime,
    flair__Comment__c: note ?? null,
    attachedFile: attachedFile ?? null,
    loomVideo: loomVideo ?? null,
    extraFields: collectFormDataExtraFields(formData).map(mapToExtraFieldInput),
    daysTimes,
    documentCategoryId: documentCategoryId ?? null,
  };
};

type Props = AbsenceFormProps & {
  validationSchema: yup.AnyObjectSchema;
  defaultValues?: AbsenceDefaultValues;
};

export const hasOrderingTimeFieldError = (errors: FieldErrors) =>
  Object.keys(errors).some((fieldName) => {
    if (!fieldName.endsWith('_startTime') && !fieldName.endsWith('_endTime')) {
      return false;
    }

    const error = getFormError(fieldName, errors);
    return (
      typeof error !== 'string' &&
      error?.key === 'requestAbsence.errors.timeError'
    );
  });

const HourlyAbsenceForm: React.FC<React.PropsWithChildren<Props>> = ({
  categoryId,
  categoryName,
  policyId,
  unlimited,
  withoutBalance,
  onClose,
  specialRules,
  absenceCategoriesGroupSelect,
  absenceCategorySelect,
  accrualPolicyInterval,
  absenceRequestPolicySelect,
  absenceRequestPolicyRules,
  requireDocumentUpload,
  minAmountForDocumentUpload,
  documentCategoryId,
  requireNote,
  notePlaceholder,
  onSuccess,
  refetchQueries,
  validationSchema,
  defaultValues,
  absenceId,
}) => {
  const { t } = useTranslation();
  const { employeeId, isManagerView } = useEmployeeIdContext();
  const { addSuccess } = useToasts();
  const { id: meId } = useUserInfo();
  const { month, year: yearStr } = useQueryParams();
  const period = month ? toMonthRange(month) : undefined;
  const year = yearStr ? parseInt(yearStr) : undefined;
  const yearPeriod = Number.isInteger(year)
    ? currentYearPeriod(year)
    : undefined;
  const myAbsencesRefetchQuery = useMyAbsencesRefetchOption(period);
  const isEditingMode = !!absenceId;

  const employeeAbsencesRefetchQuery = useEmployeeAbsencesRefetchOption(
    meId,
    yearPeriod,
  );

  const [upsertAbsence] = useUpsertAbsenceMutation({
    awaitRefetchQueries: true,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries: [
      ...(isManagerView
        ? []
        : [myAbsencesRefetchQuery, ...employeeAbsencesRefetchQuery]),
      ...(refetchQueries ?? []),
    ],
  });

  const errorHandler = useMutationErrorHandler(() =>
    t('requestAbsence.errorMessage'),
  );

  absenceFormContext.requireNote = requireNote;

  const contextParams: ExtraFieldContextParam[] = [
    {
      name: extraFieldsContextParams.absenceRequestForm.absenceCategoryName,
      type: FieldType.String,
      value: categoryName,
    },
    {
      name: extraFieldsContextParams.absenceRequestForm.employeeAbsenceCategory,
      type: FieldType.Reference,
      value: categoryId,
    },
  ];

  const form = useAbsenceFieldsForm<FormData>({
    defaultValues: getDefaultValues(defaultValues),
    absenceFormContext,
    validationSchema,
    contextParams,
  });

  const { watch } = form;
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const [absenceFile, setAbsenceFile] = useState<Maybe<FileInput>>(
    defaultValues?.attachedFile,
  );

  const [
    validateRequestAbsence,
    { loading: validating, data: validateRequestAbsenceData },
  ] = useValidateRequestAbsenceMutation();

  const validationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidationKeyRef = useRef<string>('');

  const debouncedValidation = useCallback(() => {
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    validationTimerRef.current = setTimeout(() => {
      const { startDate, endDate } = form.getValues();
      if (categoryId && startDate && endDate) {
        const currentDaysTimes = calculateDaysTimes(
          startDate,
          endDate,
          form.getValues(),
        );

        // Create a unique key for this validation to prevent duplicates
        const validationKey = `${startDate?.toISOString()}-${endDate?.toISOString()}-${JSON.stringify(
          currentDaysTimes,
        )}`;

        if (validationKey === lastValidationKeyRef.current) {
          return;
        }

        lastValidationKeyRef.current = validationKey;

        const isFormValid = form.formState.isValid;

        if (isFormValid) {
          const {
            documentCategoryId: _documentCategoryId,
            ...validationInput
          } = mapFormToInput({
            categoryId,
            policyId,
            startDate,
            endDate,
            daysTimes: normalizeDaysTimes(currentDaysTimes),
            absenceId,
            employeeId,
            documentCategoryId,
          });
          validateRequestAbsence({
            variables: {
              input: validationInput,
            },
          });
        }
      }
    }, 500);
  }, [
    categoryId,
    policyId,
    form,
    validateRequestAbsence,
    absenceId,
    employeeId,
    documentCategoryId,
  ]);

  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    debouncedValidation();
  }, [debouncedValidation, startDate, endDate]);

  const validationResult =
    validateRequestAbsenceData?.absence.validateAbsenceRequest;

  const overlappingError = useMemo(
    () =>
      validationResult?.error?.__typename ===
      'ValidateAbsenceRequestOverlappingAbsencesError'
        ? validationResult.error
        : null,
    [validationResult],
  );

  const overlappingAbsences = overlappingError?.overlappingAbsences ?? [];
  const overlappingType = overlappingError?.overlappingType;
  const hasOverlappingAbsences = overlappingAbsences?.length > 0;

  const documentUploadVisible = isDocumentUploadVisible(
    requireDocumentUpload,
    minAmountForDocumentUpload,
    validationResult?.requestedAmount,
  );

  const hasTimeFieldsError = hasOrderingTimeFieldError(form.formState.errors);

  // Block an overlapping-absence error unless the category explicitly permits
  // overlap. Fail safe: a missing/unknown type still blocks, and we don't rely
  // on the overlappingAbsences list being non-empty.
  const hasOverlappingError =
    !!overlappingError &&
    overlappingType !== AbsenceOverlappingType.Allowed &&
    overlappingType !== AbsenceOverlappingType.AllowedWithOverride;
  const hasOtherError =
    !!validationResult?.error &&
    validationResult.error.__typename !==
      'ValidateAbsenceRequestOverlappingAbsencesError';
  const hasError = hasOverlappingError || hasOtherError || hasTimeFieldsError;
  const hasInvalidFile = !!(documentUploadVisible && !absenceFile);

  const overlappingMessage = getOverlappingMessage(
    t,
    overlappingType,
    hasOverlappingAbsences,
  );

  const getError = useCallback(() => {
    if (hasTimeFieldsError) {
      return t('requestAbsence.errors.timeError');
    } else if (hasOverlappingError) {
      return overlappingMessage;
    }

    return undefined;
  }, [hasTimeFieldsError, hasOverlappingError, overlappingMessage, t]);

  const error = getError();

  const onSubmit = useCallback(
    async (values: FormData) => {
      // block submit action
      if (hasError) {
        return;
      }

      const { startDate, endDate } = form.getValues();
      const daysTimes =
        startDate && endDate
          ? calculateDaysTimes(startDate, endDate, form.getValues())
          : [];

      await upsertAbsence({
        variables: {
          input: mapFormToInput({
            ...values,
            categoryId,
            policyId,
            attachedFile: absenceFile,
            daysTimes: normalizeDaysTimes(daysTimes),
            absenceId,
            employeeId,
            documentCategoryId,
          }),
        },
      })
        .then(() => {
          addSuccess(t('requestAbsence.successMessage'));
          onSuccess && onSuccess();
          onClose();
        })
        .catch(errorHandler);
    },
    [
      hasError,
      upsertAbsence,
      categoryId,
      policyId,
      absenceFile,
      errorHandler,
      addSuccess,
      t,
      onSuccess,
      onClose,
      form,
      absenceId,
      documentCategoryId,
      employeeId,
    ],
  );

  const normalizeDaysTimes = (daysTimes: DayTime[]) => {
    return daysTimes.map((d) => ({
      currentDate: d.currentDate,
      startTime: d.startTime ? `${d.startTime}:00` : '',
      endTime: d.endTime ? `${d.endTime}:00` : '',
    }));
  };

  return (
    <ModalSidebarFormContent7
      form={form}
      onSubmit={onSubmit}
      body={
        <FormBody
          absenceCategoriesGroupSelect={absenceCategoriesGroupSelect}
          absenceCategorySelect={absenceCategorySelect}
          absenceRequestPolicySelect={absenceRequestPolicySelect}
          absenceRequestPolicyRules={absenceRequestPolicyRules}
          specialRules={
            specialRules ? (
              <SpecialRulesCard richText={specialRules} />
            ) : undefined
          }
          startDateInput={
            <ControlledDateInput name="startDate" disabled={isEditingMode} />
          }
          startDateTimeInput={
            <ControlledInput
              type="time"
              name="startTime"
              disabled={isEditingMode}
            />
          }
          endDateInput={
            <ControlledDateInput
              name="endDate"
              options={{
                minDate: form.getValues().startDate,
              }}
              disabled={isEditingMode}
            />
          }
          endDateTimeInput={
            <ControlledInput
              name="endTime"
              type="time"
              disabled={isEditingMode}
            />
          }
          fileInput={
            documentUploadVisible ? (
              <AbsenceFileField
                file={absenceFile}
                saving={validating}
                onSelectFile={setAbsenceFile}
              />
            ) : null
          }
          requireNote={requireNote}
          noteInput={
            <ControlledInput
              name="note"
              type="textarea"
              placeholder={notePlaceholder ?? ''}
              disabled={isEditingMode}
            />
          }
          requestingAmountBadge={
            <RequestingAmountResult
              data={validationResult}
              loading={validating}
              type={AbsenceType.Hourly}
              accrualPolicyInterval={accrualPolicyInterval}
              unlimited={unlimited}
              withoutBalance={withoutBalance}
              absenceStartDate={startDate}
            />
          }
          extraFields={
            //TODO:: Temp solution: The whole section is hidden now, later we can work on it.
            isEditingMode ? null : (
              <ExtraFieldsArea7
                objectApiName={ExtraFieldsSupportedObject.Absence}
                formId={ExtraFieldsFormId.AbsencesRequestAbsence}
                contextParams={contextParams}
                disabled={isEditingMode}
              />
            )
          }
          overlappingAbsences={
            hasOverlappingAbsences ? (
              <OverlappingAbsences
                absences={overlappingAbsences}
                loading={validating}
                message={overlappingMessage}
              />
            ) : undefined
          }
          isHourly={true}
          onTimeChange={debouncedValidation}
          isEditingMode={isEditingMode}
        />
      }
      footer={
        <FormButtons
          disabled={
            hasError || hasInvalidFile || validating || !form.formState.isValid
          }
          error={error}
          isEditing={isEditingMode}
        />
      }
    />
  );
};

export default HourlyAbsenceForm;
